import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { REGIMES_TRIBUTARIOS } from '@malgoos/shared';
import type { TFunction } from 'i18next';
import { Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { Sensivel } from '../../componentes/sensivel';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { api, del, mensagemDeErro, post, traduzirErro } from '../../nucleo/api';
import { discretoAtivo, mascararDocumento } from '../../nucleo/discreto';
import { data, nomePessoa } from '../../nucleo/formato';
import { consultar, estadoDaResposta, fiscalDaConsulta } from './consulta';
import type { Ficha, Fiscal, Regime } from './tipos';

const PORTES = ['mei', 'me', 'epp', 'demais'] as const;
const SITUACOES = ['ativa', 'suspensa', 'inapta', 'baixada', 'nula'] as const;
export const REGIMES: Regime[] = [...REGIMES_TRIBUTARIOS];

/**
 * Campos dos dados fiscais (o cartão da ficha e, no C5, o grupo recolhível do "Novo cadastro"). `prefixo` separa os nomes
 * no formulário da criação (`fiscal.razao_social`); `grupo` é o título da área. Lote 19/10 (item 25): data de abertura,
 * capital social, natureza jurídica e data da situação saíram da tela a pedido do Gustavo; o valor que já existe (ou que a
 * consulta do CNPJ traz) continua no estado do formulário e vai no PUT, então nada se apaga.
 */
export function camposFiscais(t: TFunction<'cadastros'>, prefixo = '', grupo?: string): DefCampo[] {
  const n = (k: string) => `${prefixo}${k}`;
  const g = grupo ? { grupo } : {};
  return [
    { nome: n('razao_social'), rotulo: t('campos.razaoSocial'), tipo: 'texto', sensivel: 'nome', ...g },
    { nome: n('nome_fantasia'), rotulo: t('campos.nomeFantasia'), tipo: 'texto', largura: 'meia', sensivel: 'nome', ...g },
    { nome: n('porte'), rotulo: t('campos.porte'), tipo: 'selecao', largura: 'meia', opcoes: PORTES.map((x) => ({ valor: x, rotulo: t(`valores.porte.${x}`) })), ...g },
    { nome: n('situacao_cadastral'), rotulo: t('campos.situacaoCadastral'), tipo: 'selecao', largura: 'meia', opcoes: SITUACOES.map((x) => ({ valor: x, rotulo: t(`valores.situacao.${x}`) })), ...g },
    { nome: n('isento_ie'), rotulo: t('campos.isentoIe'), tipo: 'booleano', largura: 'meia', ...g },
    { nome: n('inscricao_estadual'), rotulo: t('campos.inscricaoEstadual'), tipo: 'texto', largura: 'meia', visivel: (v) => !v[n('isento_ie')], sensivel: 'documento', ...g },
    { nome: n('inscricao_municipal'), rotulo: t('campos.inscricaoMunicipal'), tipo: 'texto', largura: 'meia', sensivel: 'documento', ...g },
    { nome: n('produtor_rural'), rotulo: t('campos.produtorRural'), tipo: 'booleano', largura: 'meia', ...g },
    { nome: n('inscricoes_produtor'), rotulo: t('campos.inscricoesProdutor'), tipo: 'tags', ajuda: t('campos.inscricoesAjuda'), visivel: (v) => !!v[n('produtor_rural')], ...g },
  ];
}

/** Corpo do `PUT /cadastros/parceiros/:id/fiscal` a partir dos valores do formulário (com o mesmo `prefixo`). */
export function corpoFiscal(valores: Record<string, unknown>, prefixo = ''): Partial<Fiscal> {
  const v = (k: string) => valores[`${prefixo}${k}`];
  return {
    razao_social: (v('razao_social') as string) || null,
    nome_fantasia: (v('nome_fantasia') as string) || null,
    data_abertura: (v('data_abertura') as string) || null,
    porte: (v('porte') as Fiscal['porte']) || null,
    natureza_juridica: (v('natureza_juridica') as string) || null,
    situacao_cadastral: (v('situacao_cadastral') as Fiscal['situacao_cadastral']) || null,
    situacao_data: (v('situacao_data') as string) || null,
    capital_social_centavos: typeof v('capital_social_centavos') === 'number' ? (v('capital_social_centavos') as number) : null,
    isento_ie: !!v('isento_ie'),
    inscricao_estadual: v('isento_ie') ? null : (v('inscricao_estadual') as string) || null,
    inscricao_municipal: (v('inscricao_municipal') as string) || null,
    produtor_rural: !!v('produtor_rural'),
    inscricoes_produtor: v('produtor_rural') && Array.isArray(v('inscricoes_produtor')) ? (v('inscricoes_produtor') as string[]) : [],
  };
}

/** Inscrição estadual ou municipal como foi digitada; no modo discreto, só parte dos dígitos (como os documentos). */
const inscricao = (v: string) => (discretoAtivo() ? mascararDocumento(v) : v);

/** Matriz ou filial pelo CNPJ (a ordem 0001 é a matriz): calculado, nunca digitado (vício do antigo). */
export const matrizOuFilial = (cnpj: string | null | undefined) => {
  const d = String(cnpj ?? '').replace(/\D/g, '');
  return d.length === 14 ? (d.slice(8, 12) === '0001' ? 'matriz' : 'filial') : null;
};

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 justify-between gap-4 py-1.5 text-sm">
      <dt className="shrink-0 text-tinta-3">{rotulo}</dt>
      <TextoCortado como="dd" className="min-w-0 text-right">
        {children}
      </TextoCortado>
    </div>
  );
}

/**
 * Fase 2 (W5, L7): cartão "Dados fiscais" da ficha. Porte e situação em lista, capital em centavos (no modo discreto, a
 * marca), IE com isenção (conferida pela UF do endereço principal, como aviso), IM, produtor rural e o regime tributário com
 * vigência, de onde saem "optante pelo Simples" e "MEI". Sem dado ainda: estado vazio com a ação.
 */
export function CartaoFiscal({ p, podeEditar, recarregar }: { p: Ficha; podeEditar: boolean; recarregar: () => Promise<unknown> }) {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const [editar, setEditar] = useState(false);
  const [novoRegime, setNovoRegime] = useState(false);
  const [ajuste, setAjuste] = useState<{ n: number; dados: Record<string, unknown> }>({ n: 0, dados: {} });
  const [estadoConsulta, setEstadoConsulta] = useState<string | null>(null);
  const f = p.fiscal;
  const br = !p.pais || p.pais === 'BR';
  const cnpj = br && p.tipo === 'empresa' && String(p.documento ?? '').replace(/\D/g, '').length === 14 ? p.documento : null;
  const vigente = p.regimes.find((r) => r.vigente) ?? null;
  const uf = p.enderecos.find((e) => e.tipo === 'principal')?.uf ?? null;

  const removerRegime = useMutation({
    mutationFn: (id: string) => del(`/cadastros/parceiros/${p.id}/regimes/${id}`),
    onSuccess: () => recarregar(),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  async function consultarReceita() {
    if (!cnpj) return;
    setEstadoConsulta('buscando');
    const r = await consultar('cnpj', cnpj);
    setEstadoConsulta(estadoDaResposta(r, 'cnpj'));
    if (r.situacao === 'encontrado' && r.dados) setAjuste((a) => ({ n: a.n + 1, dados: { ...fiscalDaConsulta(r.dados), capital_social_centavos: r.dados!.capital_social_centavos ?? undefined } }));
  }

  const campos = camposFiscais(t);

  async function salvar(v: Record<string, unknown>) {
    const corpo = corpoFiscal(v);
    const r = await api<{ avisos: { codigo: string; mensagem: string }[] }>('PUT', `/cadastros/parceiros/${p.id}/fiscal`, corpo);
    await recarregar();
    setEditar(false);
    setEstadoConsulta(null);
    const aviso = r?.avisos?.[0];
    if (aviso) toast.info(t('ficha.salvoComAviso', { aviso: traduzirErro(aviso.codigo, aviso.mensagem) }));
    else toast.sucesso(tn('comum.salvo'));
  }

  const rotuloPeriodo = (r: Ficha['regimes'][number]) => `${t(`valores.regime.${r.regime}`)} ${r.fim ? `${data(r.inicio)} · ${data(r.fim)}` : t('ficha.desde', { data: data(r.inicio) })}`;

  return (
    <Cartao
      titulo={t('ficha.fiscal')}
      acao={
        podeEditar && (
          <Botao variante="fantasma" tamanho="sm" icone={f ? <Pencil className="size-4" /> : <Plus className="size-4" />} onClick={() => setEditar(true)}>
            {f ? t('acoes.editarFiscal') : t('acoes.preencherFiscal')}
          </Botao>
        )
      }
    >
      {!f ? (
        <p className="text-sm text-tinta-3">{t('ficha.fiscalVazio')}</p>
      ) : (
        <dl className="grid gap-x-8 sm:grid-cols-2">
          {/* Review r16 spec M3: razão social e nome fantasia são nome de empresa (a regra por nome já os trata assim): iniciais
              com a opção de nomes, como o nome do cadastro, e não texto livre que só trocava o trecho igual ao nome. */}
          {f.razao_social && <Linha rotulo={t('campos.razaoSocial')}>{nomePessoa(f.razao_social)}</Linha>}
          {f.nome_fantasia && <Linha rotulo={t('campos.nomeFantasia')}>{nomePessoa(f.nome_fantasia)}</Linha>}
          {matrizOuFilial(p.documento) && <Linha rotulo={t('campos.matrizFilial')}>{t(`ficha.${matrizOuFilial(p.documento)}`)}</Linha>}
          {f.porte && <Linha rotulo={t('campos.porte')}>{t(`valores.porte.${f.porte}`)}</Linha>}
          {f.situacao_cadastral && <Linha rotulo={t('campos.situacaoCadastral')}>{t(`valores.situacao.${f.situacao_cadastral}`)}</Linha>}
          <Linha rotulo={t('campos.inscricaoEstadual')}>{f.isento_ie ? t('campos.isentoIe') : f.inscricao_estadual ? <span className="numero">{inscricao(f.inscricao_estadual)}</span> : '-'}</Linha>
          {f.inscricao_municipal && (
            <Linha rotulo={t('campos.inscricaoMunicipal')}>
              <span className="numero">{inscricao(f.inscricao_municipal)}</span>
            </Linha>
          )}
          {f.produtor_rural && (
            <Linha rotulo={t('campos.produtorRural')}>
              <Sensivel>{f.inscricoes_produtor.join(', ') || tn('comum.sim')}</Sensivel>
            </Linha>
          )}
        </dl>
      )}
      {f?.inscricao_estadual && !f.isento_ie && br && !uf && <p className="mt-2 text-xs text-tinta-3">{t('ficha.ieNaoConferida')}</p>}

      <div className="mt-4 border-t border-borda pt-3">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="kicker">{t('ficha.regimes')}</p>
          {podeEditar && (
            <Botao variante="fantasma" tamanho="sm" icone={<Plus className="size-4" />} onClick={() => setNovoRegime(true)}>
              {t('acoes.adicionarRegime')}
            </Botao>
          )}
        </div>
        {p.regimes.length === 0 ? (
          <p className="text-sm text-tinta-3">{t('ficha.regimesVazio')}</p>
        ) : (
          <>
            <p className="mb-2 flex flex-wrap gap-2 text-sm">
              <Selo cor={vigente && (vigente.regime === 'simples' || vigente.regime === 'mei') ? 'verde' : 'neutro'}>
                {t('ficha.simples')}: {vigente && (vigente.regime === 'simples' || vigente.regime === 'mei') ? tn('comum.sim') : tn('comum.nao')}
              </Selo>
              <Selo cor={vigente?.regime === 'mei' ? 'verde' : 'neutro'}>
                {t('ficha.mei')}: {vigente?.regime === 'mei' ? tn('comum.sim') : tn('comum.nao')}
              </Selo>
            </p>
            <ul className="divide-y divide-borda">
              {p.regimes.map((r) => (
                <li key={r.id} className="flex min-w-0 items-center justify-between gap-3 py-1.5 text-sm">
                  <span className="min-w-0">
                    {rotuloPeriodo(r)} {r.vigente && <Selo cor="verde">{t('ficha.vigente')}</Selo>}
                  </span>
                  {podeEditar && (
                    <button type="button" aria-label={`${tn('comum.excluir')} ${t(`valores.regime.${r.regime}`)}`} onClick={() => removerRegime.mutate(r.id)} className="shrink-0 rounded p-1 text-tinta-3 hover:text-perigo">
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <Modal
        aberto={editar}
        aoFechar={() => {
          setEditar(false);
          setEstadoConsulta(null);
        }}
        titulo={t('ficha.fiscal')}
        largura="lg"
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setEditar(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-fiscal">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        {cnpj && (
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Botao icone={<Search className="size-4" />} carregando={estadoConsulta === 'buscando'} onClick={() => void consultarReceita()}>
              {t('acoes.consultarCnpj')}
            </Botao>
            {estadoConsulta && (
              <p role="status" className="text-sm text-tinta-2">
                {t(`consulta.${estadoConsulta}`)}
              </p>
            )}
          </div>
        )}
        {editar && (
          <Formulario
            id="form-fiscal"
            campos={campos}
            ajuste={ajuste}
            inicial={
              f
                ? { ...f, inscricoes_produtor: f.inscricoes_produtor ?? [] }
                : { isento_ie: false, produtor_rural: false, inscricoes_produtor: [], razao_social: p.tipo === 'empresa' ? p.nome : undefined }
            }
            aoEnviar={salvar}
          />
        )}
      </Modal>

      <Modal
        aberto={novoRegime}
        aoFechar={() => setNovoRegime(false)}
        titulo={t('acoes.adicionarRegime')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setNovoRegime(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-regime">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        {novoRegime && (
          <Formulario
            id="form-regime"
            campos={[
              { nome: 'regime', rotulo: t('campos.regime'), tipo: 'selecao', obrigatorio: true, opcoes: REGIMES.map((x) => ({ valor: x, rotulo: t(`valores.regime.${x}`) })) },
              { nome: 'inicio', rotulo: t('campos.inicio'), tipo: 'data', obrigatorio: true, largura: 'meia' },
              { nome: 'fim', rotulo: t('campos.fim'), tipo: 'data', largura: 'meia' },
            ]}
            inicial={{ regime: 'simples' }}
            aoEnviar={async (v) => {
              await post(`/cadastros/parceiros/${p.id}/regimes`, { regime: v.regime, inicio: v.inicio, fim: v.fim || null });
              await recarregar();
              setNovoRegime(false);
              toast.sucesso(tn('comum.salvo'));
            }}
          />
        )}
      </Modal>
    </Cartao>
  );
}
